'use client';

import React from 'react';
import { OpenViolationCell } from '@/components/ui/OpenViolationCell';
import type { OpenViolationCellSeverity } from '@/components/ui/OpenViolationCell';
import { Divider } from '@/components/ui/Divider';
import type { ViolationRecord } from '@repo/api';
import { titleCase } from '@repo/utils';

export interface NycOpenViolationsModalProps {
  violations: ViolationRecord[];
  violationClass: 'Class A' | 'Class B' | 'Class C' | 'Class I';
}

const getViolationSeverity = (violationClass: string): OpenViolationCellSeverity => {
  switch (violationClass) {
    case 'Class C':
      return 'highest';
    case 'Class B':
      return 'high';
    case 'Class A':
      return 'medium';
    case 'Class I':
      return 'low';
    default:
      return 'medium';
  }
};

const formatDate = (dateString: string) => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

interface MonthGroupProps {
  month: string;
  violations: ViolationRecord[];
  violationClass: string;
}

function MonthGroup({ month, violations, violationClass }: MonthGroupProps) {
  console.log({ violations });
  return (
    <div className="flex flex-col gap-1">
      <div className="px-2 py-3 rounded-lg bg-primary">
        <p className="text-body-2-high text-primary">{month}</p>
      </div>

      {violations.map((violation, index) => (
        <React.Fragment key={violation.violationId}>
          <OpenViolationCell
            title={titleCase(violation.currentStatus) || 'Notice of issuance sent to tenant'}
            date={formatDate(violation.approvalDate)}
            description={violation.novDescription}
            severity={getViolationSeverity(violationClass)}
            showDescription={true}
            isOpen={violation.isOpen}
          />
          {index < violations.length - 1 && (
            <div className="px-2">
              <Divider variant="tertiary" />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export function NycOpenViolationsModal({
  violations,
  violationClass,
}: NycOpenViolationsModalProps) {
  const groupedViolations = React.useMemo(() => {
    const groups: Record<string, ViolationRecord[]> = {};

    violations.forEach(violation => {
      try {
        const date = new Date(violation.approvalDate);
        const monthYear = date.toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        });

        if (!groups[monthYear]) {
          groups[monthYear] = [];
        }
        groups[monthYear].push(violation);
      } catch {
        const fallbackKey = 'Unknown Date';
        if (!groups[fallbackKey]) {
          groups[fallbackKey] = [];
        }
        groups[fallbackKey].push(violation);
      }
    });

    const sortedEntries = Object.entries(groups).sort(([a], [b]) => {
      try {
        const dateA = new Date(a + ' 1');
        const dateB = new Date(b + ' 1');
        return dateB.getTime() - dateA.getTime();
      } catch {
        return 0;
      }
    });

    return sortedEntries;
  }, [violations]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        {groupedViolations.map(([month, monthViolations], groupIndex) => (
          <React.Fragment key={month}>
            <MonthGroup
              month={month}
              violations={monthViolations}
              violationClass={violationClass}
            />
            {groupIndex < groupedViolations.length - 1 && (
              <div className="px-2 py-1">
                <Divider variant="tertiary" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
